import * as React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Button } from './button';
import { Field, FieldError, FieldLabel } from './field';
import { Input } from './input';

export interface PromptOptions {
  title: string;
  description?: React.ReactNode;
  /** Label on the field; defaults to "Value". */
  label?: string;
  placeholder?: string;
  /** Pre-filled entry, selected on open the way the native prompt selects its default. */
  defaultValue?: string;
  /** Label of the accepting button; defaults to "Save". */
  actionLabel?: string;
  cancelLabel?: string;
  /**
   * Refuses an entry: return the message to show under the field, or `null` to accept. It runs
   * on the trimmed value, keeps the dialog open with what the user typed, and spares the caller
   * a toast that arrives after the entry is already gone.
   */
  validate?: (value: string) => string | null;
}

/**
 * Resolves the trimmed entry, or `null` on cancel, Escape, or a newer request. That is
 * `window.prompt`'s contract — empty entry and refusal stay distinguishable — without the OS
 * chrome, and awaited the same way as `useConfirm`.
 */
export type PromptFn = (request: PromptOptions | string) => Promise<string | null>;

interface PendingPrompt {
  options: PromptOptions;
  resolve: (value: string | null) => void;
  /** The control that asked, so focus returns there; there is no Radix trigger to do it. */
  returnFocus: HTMLElement | null;
}

const PromptContext = React.createContext<PromptFn | null>(null);

/**
 * Hosts the single text-entry dialog behind `usePrompt`. `UiProvider` mounts it beside the
 * confirm dialog, so every shell and `renderInForgeRoot` test can call
 * `await prompt('Name for the new preset')` where `window.prompt` used to sit.
 *
 * It is built on the Alert Dialog rather than the Dialog even though it carries a field: the
 * page keeps running behind it, and the page-level shortcut listeners recognise an open
 * decision by `[role="alertdialog"]` through `isInsideAlertDialog`. On a plain dialog role,
 * Escape would close the prompt and still reach the page's own Escape and crop shortcuts.
 */
export function PromptProvider({ children }: React.PropsWithChildren) {
  // `pending` outlives `open` so the dialog keeps its content through the exit animation.
  const [pending, setPending] = React.useState<PendingPrompt | null>(null);
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const pendingRef = React.useRef<PendingPrompt | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const fieldId = React.useId();
  const errorId = `${fieldId}-error`;

  const settle = React.useCallback((entry: string | null) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    current?.resolve(entry);
  }, []);

  React.useEffect(
    () => () => {
      pendingRef.current?.resolve(null);
      pendingRef.current = null;
    },
    []
  );

  const prompt = React.useCallback<PromptFn>((request) => {
    const options = typeof request === 'string' ? { title: request } : request;
    return new Promise<string | null>((resolve) => {
      pendingRef.current?.resolve(null);
      // `document.body` is an HTMLElement and is always connected, so capturing it would make
      // the handler below preventDefault and focus <body> — exactly the drop this avoids.
      const active = document.activeElement;
      const returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;
      const next = { options, resolve, returnFocus };
      pendingRef.current = next;
      setPending(next);
      setValue(options.defaultValue ?? '');
      setError(null);
      setOpen(true);
    });
  }, []);

  const options = pending?.options;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const entry = value.trim();
    const message = options?.validate?.(entry) ?? null;
    if (message !== null) {
      setError(message);
      inputRef.current?.focus();
      return;
    }
    settle(entry);
  };

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) settle(null);
        }}
      >
        {options ? (
          <AlertDialogContent
            size="sm"
            onOpenAutoFocus={(event) => {
              // The entry is the point of the dialog; the Alert Dialog would focus Cancel.
              event.preventDefault();
              inputRef.current?.focus();
              inputRef.current?.select();
            }}
            onCloseAutoFocus={(event) => {
              const target = pending?.returnFocus;
              if (target?.isConnected) {
                event.preventDefault();
                target.focus();
              }
            }}
            {...(options.description ? {} : { 'aria-describedby': undefined })}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{options.title}</AlertDialogTitle>
              {options.description ? (
                <AlertDialogDescription>{options.description}</AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
            {/* A form, so Enter accepts the entry the way it does in the native prompt. */}
            <form className="grid gap-4" onSubmit={handleSubmit}>
              <Field>
                <FieldLabel htmlFor={fieldId}>{options.label ?? 'Value'}</FieldLabel>
                <Input
                  id={fieldId}
                  ref={inputRef}
                  value={value}
                  placeholder={options.placeholder}
                  aria-invalid={error !== null}
                  aria-describedby={error !== null ? errorId : undefined}
                  onChange={(event) => {
                    setValue(event.target.value);
                    setError(null);
                  }}
                />
                {error !== null ? <FieldError id={errorId}>{error}</FieldError> : null}
              </Field>
              <AlertDialogFooter>
                <AlertDialogCancel type="button" onClick={() => settle(null)}>
                  {options.cancelLabel ?? 'Cancel'}
                </AlertDialogCancel>
                <Button type="submit">{options.actionLabel ?? 'Save'}</Button>
              </AlertDialogFooter>
            </form>
          </AlertDialogContent>
        ) : null}
      </AlertDialog>
    </PromptContext.Provider>
  );
}

export function usePrompt(): PromptFn {
  const prompt = React.useContext(PromptContext);
  if (!prompt) {
    throw new Error('usePrompt needs a UiProvider above it (every shell and renderInForgeRoot).');
  }
  return prompt;
}
